import { useState, useCallback, useRef } from 'react';
import { useAIStream } from './useAIStream';
import { 
  createStreamingAccumulator, 
  addChunk, 
  getCompletionPercentage 
} from '../utils/streamingCodeAccumulator';
import { apiService } from '../services/api';
import { AIModel } from '../components/editor/ModelSelector';
import { VisualStyle } from '../components/editor/StyleSelector';
import { PromptItemData } from '../utils/promptItemContext';
import { analyticsService } from '../services/analyticsService';

const EDITOR_CONFIG = {
  AI_PATCH_TIMEOUT: 240000,
  HISTORY_FETCH_DELAY: 1000,
};

interface AIGenerationCallbacks {
  onCodeUpdate: (code: string) => void;
  onPromptClear: () => void;
  onUsageUpdate: (usage: any) => void;
  onSuggestionsUpdate: (suggestions: any[]) => void;
  onChatFormatUpdate: (format: string) => void;
  onSessionUpdate: (sessionId: string) => void;
  onHistoryRefresh: (sessionId?: string) => void;
  onProjectDataRefresh: () => void;
  onEditHistoryRefresh: () => void;
  onError: (message: string) => void;
  onOptimisticUpdate: (message: any) => void;
}

/**
 * Hook to manage AI generation with streaming and non-streaming modes
 * Handles all AI-related state and callbacks
 */
export function useAIGeneration(
  projectId: string | null,
  sessionId: string | null,
  enableStreaming: boolean = true,
  externalCallbacks?: {
    onSessionUpdate?: (sessionId: string) => void;
    onHistoryRefresh?: (sessionId?: string) => void;
    onProjectDataRefresh?: () => void;
    onEditHistoryRefresh?: () => void;
  }
) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [processingUpdateMessage, setProcessingUpdateMessage] = useState<string | null>(null);
  const [streamingAccumulator, setStreamingAccumulator] = useState(createStreamingAccumulator());
  const [streamingProgress, setStreamingProgress] = useState(0);
  
  // Track if user aborted to prevent clearing messages
  const userAbortedRef = useRef(false);
  
  // Streaming chat messages state (for [CHAT] blocks)
  interface ChatMessage {
    id: string;
    timestamp: string;
    content: string;
    sequenceNumber: number;
    isComplete: boolean;
    isWarning?: boolean;  // For abort messages
  }
  const [streamingChatMessages, setStreamingChatMessages] = useState<ChatMessage[]>([]);
  
  // Code edit progress state (for [CODE_EDIT] blocks)
  interface CodeEditProgress {
    id: string;
    timestamp: string;
    processingMessage: string;
    completeMessage: string | null;
    estimatedLines: number;
    currentLines: number;
    percentage: number;
    isComplete: boolean;
    isApplied: boolean;
    isError?: boolean;
    isAborted?: boolean;  // NEW: Mark as aborted by user
    sequenceNumber: number;
  }
  const [streamingCodeEdits, setStreamingCodeEdits] = useState<CodeEditProgress[]>([]);
  
  // Track global sequence for rendering order
  const sequenceCounterRef = useRef(0);
  
  // Track if we've received first chunk (to hide processing panel)
  const hasReceivedFirstChunkRef = useRef(false);
  
  // Legacy single-edit state (for backwards compatibility)
  const [codeEditSummary, setCodeEditSummary] = useState<string | null>(null);
  const [codeEditCount, setCodeEditCount] = useState<number | null>(null);
  const [codeEditCompleteSummary, setCodeEditCompleteSummary] = useState<string | null>(null);
  const [codeEditLinesReceived, setCodeEditLinesReceived] = useState<number>(0);
  const [codeEditComplete, setCodeEditComplete] = useState<boolean>(false);
  
  // Store current callbacks in ref so onComplete can access them
  const callbacksRef = useRef<AIGenerationCallbacks | null>(null);
  
  // Track request start time for analytics
  const requestStartTimeRef = useRef<number>(Date.now());
  
  // Store model and style for analytics
  const currentModelRef = useRef<AIModel>('norm');
  const currentStyleRef = useRef<VisualStyle>('beemi');

  /**
   * Centralized function to reset all streaming state
   * Used by: onComplete, onError, and abortStream
   */
  const resetStreamingState = useCallback((immediate: boolean = false) => {
    const resetFn = () => {
      setStreamingAccumulator(createStreamingAccumulator());
      setStreamingProgress(0);
      setStreamingChatMessages([]);
      setStreamingCodeEdits([]);
      setCodeEditSummary(null);
      setCodeEditCount(null);
      setCodeEditCompleteSummary(null);
      setCodeEditLinesReceived(0);
      setCodeEditComplete(false);
      hasReceivedFirstChunkRef.current = false;
      sequenceCounterRef.current = 0;
    };

    // For abort, reset immediately. For complete/error, delay to show final state
    if (immediate) {
      resetFn();
    } else {
      setTimeout(resetFn, EDITOR_CONFIG.HISTORY_FETCH_DELAY + 500);
    }
  }, []);

  // Initialize streaming hook with callbacks
  const { streamRequest, abort: abortStreamInternal } = useAIStream({
    onChunk: (data: any) => {
      // Handle chat message events (progressive updates)
      if (data.type === 'chat_message') {
        // Hide processing panel on first chunk
        if (!hasReceivedFirstChunkRef.current) {
          hasReceivedFirstChunkRef.current = true;
          setIsProcessing(false);
        }
        
        setStreamingChatMessages(prev => {
          // Check if this message already exists
          const existingIndex = prev.findIndex(msg => msg.id === data.id);
          
          if (existingIndex >= 0) {
            // Update existing message
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              content: data.content,
              timestamp: data.timestamp,
              isComplete: data.isComplete
            };
            return updated;
          } else {
            // New message - assign sequence number
            const sequenceNumber = sequenceCounterRef.current++;
            const updated = [...prev, { 
              id: data.id,
              timestamp: data.timestamp,
              content: data.content,
              sequenceNumber,
              isComplete: data.isComplete
            }];
            return updated;
          }
        });
        return;
      }
      
      // Handle code edit progress events
      if (data.type === 'code_edit_start') {
        console.log('[CODE_EDIT_PROGRESS] 📝 Start event received');
        console.log('[CODE_EDIT_PROGRESS] ID:', data.id);
        console.log('[CODE_EDIT_PROGRESS] Processing:', data.processingMessage);
        console.log('[CODE_EDIT_PROGRESS] Estimated:', data.estimatedLines);
        
        // Hide processing panel on first chunk
        if (!hasReceivedFirstChunkRef.current) {
          hasReceivedFirstChunkRef.current = true;
          setIsProcessing(false);
          console.log('[CODE_EDIT_PROGRESS] 🎉 First chunk received - hiding processing panel');
        }
        
        setStreamingCodeEdits(prev => {
          // Check if this edit already exists (retry scenario)
          const existingIndex = prev.findIndex(edit => edit.id === data.id);
          
          if (existingIndex >= 0) {
            // Reset existing edit for retry
            console.log(`[CODE_EDIT_PROGRESS] 🔄 Resetting existing edit ${data.id} for retry`);
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              timestamp: data.timestamp,
              processingMessage: data.processingMessage,
              completeMessage: null,
              estimatedLines: data.estimatedLines,
              currentLines: 0,
              percentage: 0,
              isComplete: false,
              isApplied: false,
              isError: false
            };
            return updated;
          } else {
            // New edit - add to array
            const sequenceNumber = sequenceCounterRef.current++;
            console.log(`[CODE_EDIT_PROGRESS] ➕ Added new edit ${data.id} with seq ${sequenceNumber}`);
            return [
              ...prev,
              {
                id: data.id,
                timestamp: data.timestamp,
                processingMessage: data.processingMessage,
                completeMessage: null,
                estimatedLines: data.estimatedLines,
                currentLines: 0,
                percentage: 0,
                isComplete: false,
                isApplied: false,
                sequenceNumber
              }
            ];
          }
        });
        
        // Update legacy state for backwards compatibility
        setCodeEditSummary(data.processingMessage);
        setCodeEditCount(data.estimatedLines);
        setCodeEditLinesReceived(0);
        setCodeEditComplete(false);
        return;
      }
      
      if (data.type === 'code_edit_progress') {
        console.log('[CODE_EDIT_PROGRESS] 📊 Progress:', `${data.percentage}% (${data.id})`);
        
        // Update specific code edit in array
        setStreamingCodeEdits(prev => prev.map(edit => 
          edit.id === data.id
            ? { ...edit, currentLines: data.currentLines, percentage: data.percentage, timestamp: data.timestamp }
            : edit
        ));
        
        // Update legacy state (use first edit)
        const firstEdit = streamingCodeEdits[0];
        if (firstEdit && firstEdit.id === data.id) {
          setCodeEditLinesReceived(data.currentLines);
        }
        return;
      }
      
      if (data.type === 'code_edit_complete') {
        console.log('[CODE_EDIT_PROGRESS] ✅ Complete:', data.completeMessage, `(${data.id})`);
        
        // Update specific code edit in array - complete but not yet applied
        setStreamingCodeEdits(prev => prev.map(edit => 
          edit.id === data.id
            ? { ...edit, completeMessage: data.completeMessage, isComplete: true, isApplied: false, percentage: 100, isError: false, timestamp: data.timestamp }
            : edit
        ));
        
        // Update legacy state (use first edit)
        const firstEdit = streamingCodeEdits[0];
        if (firstEdit && firstEdit.id === data.id) {
          setCodeEditCompleteSummary(data.completeMessage);
          setCodeEditComplete(true);
          if (codeEditCount) {
            setCodeEditLinesReceived(codeEditCount);
          }
        }
        return;
      }
      
      // Handle code edit applied event
      if (data.type === 'code_edit_applied') {
        const status = data.isError ? '❌' : '✓';
        console.log(`[CODE_EDIT_PROGRESS] ${status} Applied:`, data.id, data.isError ? '(with errors)' : '');
        
        // Mark specific code edit as applied (with error status if present)
        setStreamingCodeEdits(prev => prev.map(edit => 
          edit.id === data.id
            ? { ...edit, isApplied: true, isError: data.isError || false, completeMessage: data.completeMessage || edit.completeMessage, timestamp: data.timestamp }
            : edit
        ));
        return;
      }
      
      // Handle processing update event (e.g., "Finalizing changes", "Fixing response format issues")
      if (data.type === 'processing_update') {
        const message = data.content || 'Processing...';
        console.log('[PROCESSING_UPDATE] 🎯', message);
        setProcessingUpdateMessage(message);
        setIsProcessing(true); // Show processing panel
        return;
      }
      
      // Legacy chunk handling
      if ('chunkIndex' in data) {
        console.log('🌊 LEGACY CHUNK:', data.chunkIndex);
        return;
      }
      
      console.warn('[onChunk] Unknown event type or format:', data);
      
      // Legacy chunk handling (will be removed later)
      console.log('🌊 CHUNK RECEIVED:', {
        chunkIndex: data.chunkIndex,
        contentLength: data.content?.length,
        accumulatedLength: data.accumulated?.length,
        preview: data.content?.substring(0, 100)
      });
      
      // Use the FULL accumulated content from SSE
      const newAccumulator = addChunk({
        accumulated: data.accumulated,
        parsed: streamingAccumulator.parsed
      }, '');
      setStreamingAccumulator(newAccumulator);
      
      // Extract code edit info from accumulator
      const { parsed } = newAccumulator;
      
      console.log('[CODE_EDIT_PROGRESS] 🔍 Parse result:', {
        hasSummary: !!parsed.codeEditSummary,
        hasCount: !!parsed.codeEditCount,
        hasComplete: !!parsed.codeEditCompleteSummary,
        summary: parsed.codeEditSummary,
        count: parsed.codeEditCount
      });
      
      if (parsed.codeEditSummary) {
        console.log('[CODE_EDIT_PROGRESS] 📝 Summary:', parsed.codeEditSummary);
        setCodeEditSummary(parsed.codeEditSummary);
      }
      if (parsed.codeEditCount) {
        console.log('[CODE_EDIT_PROGRESS] 🔢 Estimated lines:', parsed.codeEditCount);
        setCodeEditCount(parsed.codeEditCount);
      }
      if (parsed.codeEditCompleteSummary) {
        console.log('[CODE_EDIT_PROGRESS] ✅ Complete summary:', parsed.codeEditCompleteSummary);
        setCodeEditCompleteSummary(parsed.codeEditCompleteSummary);
      }
      
      // Update lines received from accumulator
      setCodeEditLinesReceived(parsed.linesReceived);
      if (parsed.codeEditCount && parsed.linesReceived > 0) {
        const lineProgress = Math.min(Math.round((parsed.linesReceived / parsed.codeEditCount) * 100), 99);
        console.log(`[CODE_EDIT_PROGRESS] 📊 ${lineProgress}% (${parsed.linesReceived}/${parsed.codeEditCount} lines)`);
      }
      
      // Update general progress
      const progress = getCompletionPercentage(newAccumulator);
      setStreamingProgress(progress);
      console.log('📊 Overall progress:', progress + '%');
    },
    onSession: (data) => {
      console.log('🔗 SESSION EVENT:', data);
      // Capture session ID
      if (data.sessionId && externalCallbacks?.onSessionUpdate) {
        externalCallbacks.onSessionUpdate(data.sessionId);
      }
    },
    onComplete: (data) => {
      console.log('✅ STREAMING COMPLETE:', {
        codeLength: data.code?.length || 0,
        explanationLength: data.explanation?.length || 0,
        suggestionsCount: data.suggestions?.length || 0,
        chatFormat: data.chatFormat,
        codeEditSummary: data.codeEditSummary,
        codeEditCount: data.codeEditCount,
        codeEditCompleteSummary: data.codeEditCompleteSummary,
        metadata: data.metadata
      });
      
      // Track AI prompt completion
      analyticsService.track({
        name: 'ai_prompt_completed',
        properties: {
          session_id: sessionId || data.sessionId || null,
          model: currentModelRef.current,
          style: currentStyleRef.current,
          code_length: data.code?.length || 0,
          is_conversational_only: data.conversationalOnly || false,
          completion_time_ms: Date.now() - requestStartTimeRef.current,
          total_edits: data.codeEditCount || 0
        }
      });
      
      // Update code edit summary from complete data (only if not conversational-only)
      if (!data.conversationalOnly) {
        if (data.codeEditSummary) {
          console.log('[CODE_EDIT_PROGRESS] 📝 From complete:', data.codeEditSummary);
          setCodeEditSummary(data.codeEditSummary);
        }
        if (data.codeEditCount) {
          console.log('[CODE_EDIT_PROGRESS] 🔢 From complete:', data.codeEditCount);
          setCodeEditCount(data.codeEditCount);
        }
        if (data.codeEditCompleteSummary) {
          console.log('[CODE_EDIT_PROGRESS] ✅ From complete:', data.codeEditCompleteSummary);
          setCodeEditCompleteSummary(data.codeEditCompleteSummary);
        }
        
        // Mark code edit as complete and update lines received
        setCodeEditComplete(true);
        if (data.codeEditCount) {
          setCodeEditLinesReceived(data.codeEditCount);
        }
      }
      
      // Call parent callbacks if available
      if (callbacksRef.current) {
        const callbacks = callbacksRef.current;
        
        // Update code in editor (only if code was actually modified)
        if (data.code && !data.conversationalOnly) {
          callbacks.onCodeUpdate(data.code);
          console.log('💾 Code applied:', data.code.substring(0, 200) + '...');
        } else if (data.conversationalOnly) {
          console.log('💬 Conversational-only response - no code changes');
        }
        
        // Clear prompt
        callbacks.onPromptClear();
        
        // Update usage data
        if (data.budget) {
          callbacks.onUsageUpdate(data.budget);
          console.log('💰 Budget updated:', data.budget);
        }
        
        // Update suggestions (only if code was modified)
        if (data.suggestions && data.suggestions.length > 0 && !data.conversationalOnly) {
          callbacks.onSuggestionsUpdate(data.suggestions);
          console.log('💡 Suggestions updated:', data.suggestions);
        }
        
        // Update chat format (only if code was modified)
        if (data.chatFormat && !data.conversationalOnly) {
          callbacks.onChatFormatUpdate(data.chatFormat);
          console.log('💬 Chat format:', data.chatFormat);
        }
        
        // Capture sessionId and refresh history
        if (data.sessionId) {
          if (data.sessionId !== sessionId) {
            callbacks.onSessionUpdate(data.sessionId);
          }
          callbacks.onHistoryRefresh(data.sessionId);
        }
        
        // Refresh project and edit history
        callbacks.onProjectDataRefresh();
        callbacks.onEditHistoryRefresh();
      }
      
      // Mark all streaming code edits with error status if validation/parsing failed
      if (data.hasErrors) {
        console.log('⚠️ Marking streaming code edits as failed (hasErrors: true)');
        setStreamingCodeEdits(prev => prev.map(edit => ({
          ...edit,
          isError: true // Mark all code edits as failed
        })));
      }
      
      // Stop streaming immediately
      setIsStreaming(false);
      setIsProcessing(false);
      setProcessingUpdateMessage(null);
      
      // Clear streaming state after history has loaded (unless user aborted)
      if (!userAbortedRef.current) {
        resetStreamingState(false);
      } else {
        console.log('🛑 Skipping state reset - user aborted, keeping messages visible');
      }
      
      console.log('🎉 Streaming complete! Check logs above for chunks.');
    },
    onError: (data) => {
      console.error('❌ STREAMING ERROR:', data.error);
      
      // Track AI prompt failure
      analyticsService.track({
        name: 'ai_prompt_failed',
        properties: {
          session_id: sessionId || data.sessionId || null,
          model: currentModelRef.current,
          style: currentStyleRef.current,
          error_type: 'streaming_error',
          error_message: data.error,
          prompt_length: 0
        }
      });
      
      // Call parent error callback if available
      if (callbacksRef.current) {
        callbacksRef.current.onError(data.error);
        
        // Fetch history to show error (includes previously streamed messages + error)
        if (data.sessionId) {
          setTimeout(() => callbacksRef.current?.onHistoryRefresh(data.sessionId), EDITOR_CONFIG.HISTORY_FETCH_DELAY);
        } else if (sessionId) {
          setTimeout(() => callbacksRef.current?.onHistoryRefresh(), EDITOR_CONFIG.HISTORY_FETCH_DELAY);
        }
      }
      
      // Stop streaming but keep messages visible until history loads
      setIsStreaming(false);
      setIsProcessing(false);
      setProcessingUpdateMessage(null);
      
      // Clear streaming state after history has time to load (unless user aborted)
      if (!userAbortedRef.current) {
        resetStreamingState(false);
      } else {
        console.log('🛑 Skipping state reset - user aborted, keeping messages visible');
      }
    }
  });

  /**
   * Apply AI prompt - handles both streaming and non-streaming modes
   * @param type - 'generate' (default) or 'fix' pipeline
   * @param observedErrors - Error descriptions for fix pipeline
   * @param promptItems - Enriched prompt items with context (sent separately to server)
   */
  const handleApplyPrompt = useCallback(async (
    prompt: string,
    code: string,
    selectedModel: AIModel,
    selectedStyle: VisualStyle,
    callbacks: AIGenerationCallbacks,
    type: 'generate' | 'fix' = 'generate',
    observedErrors?: string,
    promptItems?: PromptItemData[]
  ) => {
    if (!prompt.trim()) {
      callbacks.onError('Please enter a prompt');
      return;
    }

    // Validate fix pipeline requirements
    if (type === 'fix' && !observedErrors) {
      callbacks.onError('observedErrors is required when using fix pipeline');
      return;
    }

    // Store callbacks in ref so onComplete can access them
    callbacksRef.current = callbacks;
    
    // Reset abort flag for new request
    userAbortedRef.current = false;
    
    // Track request start time and model/style for analytics
    requestStartTimeRef.current = Date.now();
    currentModelRef.current = selectedModel;
    currentStyleRef.current = selectedStyle;

    setIsProcessing(true);

    // Optimistically add user prompt to chat history
    const userMessage = {
      role: 'user',
      summary: prompt.trim(),
      timestamp: new Date().toISOString(),
      model: null
    };
    callbacks.onOptimisticUpdate(userMessage);
    console.log('Added user prompt to chat optimistically');
    
    // Track AI prompt submission
    analyticsService.track({
      name: 'ai_prompt_submitted',
      properties: {
        prompt_length: prompt.trim().length,
        has_selected_element: (promptItems && promptItems.length > 0) || false,
        prompt_items_count: promptItems?.length || 0,
        session_id: sessionId,
        type,
        model: selectedModel,
        style: selectedStyle,
        is_streaming: enableStreaming
      }
    });

    // Conditional: Use streaming if enabled
    if (enableStreaming) {
      // === STREAMING MODE ===
      console.log('🌊 Using streaming mode');
      setIsStreaming(true);
      setProcessingUpdateMessage(null);
      sequenceCounterRef.current = 0; // Reset sequence counter for new stream
      hasReceivedFirstChunkRef.current = false; // Reset first chunk flag
      setStreamingAccumulator(createStreamingAccumulator());
      setStreamingChatMessages([]);
      setStreamingCodeEdits([]);
      setCodeEditSummary(null);
      setCodeEditCount(null);
      setCodeEditCompleteSummary(null);
      setCodeEditLinesReceived(0);
      setCodeEditComplete(false);

      const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

      try {
        const requestBody: any = {
          prompt: prompt.trim(),
          code: code,
          projectId: projectId || '11111111-1111-1111-1111-111111111111',
          model: selectedModel,
          style: selectedStyle
        };

        // Add type and observedErrors for fix pipeline
        if (type === 'fix') {
          requestBody.type = 'fix';
          requestBody.observedErrors = observedErrors;
          console.log('🔧 Using Fix Pipeline with observed errors');
        }

        // Add prompt items with context if available
        if (promptItems && promptItems.length > 0) {
          requestBody.promptItems = promptItems;
          console.log('📎 Including prompt items:', promptItems.length);
        }

        await streamRequest(`${API_BASE}/api/editor/ai/patch/stream`, requestBody);
        
        // Streaming callbacks handle the rest
      } catch (error) {
        console.error('Streaming error in handleApplyPrompt:', error);
      }

      return;
    }

    // === NON-STREAMING MODE (Legacy) ===
    try {
      console.log('Sending AI request with prompt:', prompt);

      const requestBody: any = {
        prompt: prompt.trim(),
        code: code,
        projectId: projectId || '11111111-1111-1111-1111-111111111111',
        model: selectedModel,
        style: selectedStyle
      };

      // Add type and observedErrors for fix pipeline
      if (type === 'fix') {
        requestBody.type = 'fix';
        requestBody.observedErrors = observedErrors;
        console.log('🔧 Using Fix Pipeline (non-streaming) with observed errors');
      }

      // Add prompt items with context if available
      if (promptItems && promptItems.length > 0) {
        requestBody.promptItems = promptItems;
        console.log('📎 Including prompt items (non-streaming):', promptItems.length);
      }

      const response = await apiService.post('/editor/ai/patch', requestBody, {
        timeout: EDITOR_CONFIG.AI_PATCH_TIMEOUT
      });

      console.log('AI response received:', response.data);

      if (response.data.success && response.data.code) {
        callbacks.onCodeUpdate(response.data.code);
        callbacks.onPromptClear();

        if (response.data.budget) {
          callbacks.onUsageUpdate(response.data.budget);
        }

        if (response.data.suggestions && response.data.suggestions.length > 0) {
          callbacks.onSuggestionsUpdate(response.data.suggestions);
          console.log('Updated suggestions from AI response:', response.data.suggestions);
        }

        if (response.data.chatFormat) {
          callbacks.onChatFormatUpdate(response.data.chatFormat);
          console.log('Updated chat format from AI response:', response.data.chatFormat);
        }

        if (response.data.sessionId) {
          if (response.data.sessionId !== sessionId) {
            callbacks.onSessionUpdate(response.data.sessionId);
          }
          callbacks.onHistoryRefresh(response.data.sessionId);
        }

        callbacks.onProjectDataRefresh();
        callbacks.onEditHistoryRefresh();
      } else {
        const errorMsg = response.data.error || 'Failed to process AI request';
        callbacks.onError(errorMsg);

        if (response.data.sessionId) {
          setTimeout(() => callbacks.onHistoryRefresh(response.data.sessionId), EDITOR_CONFIG.HISTORY_FETCH_DELAY);
        }
      }
    } catch (err: any) {
      console.error('AI request error:', err);

      let errorMsg = '';
      let errorType = 'request_error';
      if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.message) {
        errorMsg = err.message;
        errorType = 'network_error';
      } else {
        errorMsg = 'Failed to connect to AI service. Please make sure the server is running.';
        errorType = 'network_error';
      }
      
      // Track AI prompt failure
      analyticsService.track({
        name: 'ai_prompt_failed',
        properties: {
          session_id: sessionId || err.response?.data?.sessionId || null,
          model: currentModelRef.current,
          style: currentStyleRef.current,
          error_type: errorType,
          error_message: errorMsg,
          prompt_length: prompt.length
        }
      });
      
      callbacks.onError(errorMsg);

      if (err.response?.data?.sessionId) {
        setTimeout(() => callbacks.onHistoryRefresh(err.response.data.sessionId), 1000);
      } else if (sessionId) {
        setTimeout(() => callbacks.onHistoryRefresh(), EDITOR_CONFIG.HISTORY_FETCH_DELAY);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [enableStreaming, projectId, sessionId, streamRequest, codeEditCount, streamingAccumulator]);

  // Wrap abort to also clear isProcessing state
  const abortStream = useCallback(async () => {
    console.log('🛑 AI generation aborted by user');
    
    // Track AI prompt aborted
    analyticsService.track({
      name: 'ai_prompt_aborted',
      properties: {
        session_id: sessionId || null,
        model: currentModelRef.current,
        style: currentStyleRef.current,
        abort_time_ms: Date.now() - requestStartTimeRef.current,
        chunks_received: streamingChatMessages.length + streamingCodeEdits.length
      }
    });
    
    // Set abort flag to prevent onComplete/onError from clearing messages
    userAbortedRef.current = true;
    
    // Abort the network request
    abortStreamInternal();
    
    // Stop processing/streaming flags
    setIsStreaming(false);
    setIsProcessing(false);
    setProcessingUpdateMessage(null);
    
    // Mark all streaming code edits as aborted (keep original messages, just mark status)
    setStreamingCodeEdits(prev => prev.map(edit => ({
      ...edit,
      isAborted: true,
      isComplete: true,  // Mark as complete so it doesn't show progress
      isError: false
      // Keep original completeMessage or processingMessage - don't override
    })));
    
    // Add an abort message to chat (keep existing messages visible)
    setStreamingChatMessages(prev => {
      // Always add abort message, even if no previous messages
      return [
        ...prev,
        {
          id: `abort-${Date.now()}`,
          timestamp: new Date().toISOString(),
          content: 'Request aborted by user',
          sequenceNumber: sequenceCounterRef.current++,
          isComplete: true,
          isWarning: true  // Mark as warning for yellow styling
        }
      ];
    });
    
    // Backend will save what it already streamed (no need to send it back!)
    if (sessionId) {
      try {
        await apiService.post('/editor/ai/abort', { sessionId });
        console.log('✅ Backend saving streamed content on abort');
      } catch (error) {
        console.error('❌ Failed to notify backend of abort:', error);
        // Don't fail the abort if backend notification fails
      }
    }
    
    // Reset accumulators and progress, but KEEP chat/code edit messages visible
    setStreamingAccumulator(createStreamingAccumulator());
    setStreamingProgress(0);
    setCodeEditSummary(null);
    setCodeEditCount(null);
    setCodeEditCompleteSummary(null);
    setCodeEditLinesReceived(0);
    setCodeEditComplete(false);
    hasReceivedFirstChunkRef.current = false;
    
    // Note: Do NOT call resetStreamingState() - keep messages visible
    // They will be naturally replaced by history on the next AI request
  }, [abortStreamInternal, sessionId]);

  return {
    isProcessing,
    isStreaming,
    processingUpdateMessage,
    streamingProgress,
    // New multi-block state
    streamingChatMessages,
    streamingCodeEdits,
    // Legacy single-edit state (backwards compatibility)
    codeEditSummary,
    codeEditCount,
    codeEditCompleteSummary,
    codeEditLinesReceived,
    codeEditComplete,
    handleApplyPrompt,
    abortStream
  };
}

